import { useCallback, useEffect, useState } from 'react'
import {
  clearGuestSession,
  loadGuestSession,
  saveGuestSession,
} from '@/lib/guestStorage'
import { generateUUID } from '@/lib/utils'
import type { ChatMessageWithTimestamp } from '@/types/chat/ChatMessage'
import type { GuestSession } from '@/types/GuestSession'

const GUEST_MESSAGE_LIMIT = 3

/**
 * Creates an initial guest session with a welcome message
 */
function createInitialGuestSession(): GuestSession {
  const initialMessage: ChatMessageWithTimestamp = {
    role: 'assistant',
    content: 'may i meet you?',
    timestamp: new Date(),
    id: generateUUID(),
  }

  const session: GuestSession = {
    id: 'default',
    messages: [initialMessage],
    messageCount: 0, // Only count user messages
    createdAt: new Date().toISOString(),
    isGuestMode: true,
  }

  return session
}

export interface UseGuestSessionReturn {
  guestSession: GuestSession | null
  addGuestMessage: (message: ChatMessageWithTimestamp) => void
  clearSession: () => void
  canSendMessage: () => boolean // false if >= 2 messages
  getGuestMessages: () => ChatMessageWithTimestamp[]
  userMessageCount: number
}

/**
 * Hook for managing guest session state (client-side only, localStorage-backed)
 */
export function useGuestSession(): UseGuestSessionReturn {
  const [guestSession, setGuestSession] = useState<GuestSession | null>(() => {
    // Initialize from localStorage on mount, or create a new session with welcome message
    const existingSession = loadGuestSession()
    if (existingSession) {
      return existingSession
    }

    // Create and save initial session with welcome message
    const initialSession = createInitialGuestSession()
    saveGuestSession(initialSession)
    return initialSession
  })

  // Sync to localStorage whenever session changes
  useEffect(() => {
    if (guestSession) {
      saveGuestSession(guestSession)
    }
  }, [guestSession])

  /**
   * Adds a message to the guest session
   */
  const addGuestMessage = useCallback((message: ChatMessageWithTimestamp) => {
    setGuestSession((prev) => {
      // Create new session if none exists
      if (!prev) {
        const newSession: GuestSession = {
          id: 'default',
          messages: [message],
          messageCount: message.role === 'user' ? 1 : 0,
          createdAt: new Date().toISOString(),
          isGuestMode: true,
        }
        return newSession
      }

      // Add message to existing session
      const updatedMessages = [...prev.messages, message]
      const updatedCount =
        message.role === 'user' ? prev.messageCount + 1 : prev.messageCount

      return {
        ...prev,
        messages: updatedMessages,
        messageCount: updatedCount,
      }
    })
  }, [])

  /**
   * Clears the guest session
   */
  const clearSession = useCallback(() => {
    clearGuestSession()
    setGuestSession(null)
  }, [])

  /**
   * Checks if user can send another message (under limit)
   */
  const canSendMessage = useCallback(() => {
    if (!guestSession) {
      return true // No session yet, can send
    }
    return guestSession.messageCount < GUEST_MESSAGE_LIMIT
  }, [guestSession])

  /**
   * Returns all messages in the guest session
   */
  const getGuestMessages = useCallback((): ChatMessageWithTimestamp[] => {
    return guestSession?.messages ?? []
  }, [guestSession])

  return {
    guestSession,
    addGuestMessage,
    clearSession,
    canSendMessage,
    getGuestMessages,
    userMessageCount: guestSession?.messageCount ?? 0,
  }
}
